import type { CaseRole } from './cases'
import { apiClient } from './client'
import { humintPath } from './humint-path'
import type { SubjectSummaryDto } from './humint-subject-page'
import type { VoiceConditionDto, VoicePairStatusDto } from './humint-voice-matching'

export interface RecordingVoiceMatchSummary {
  match_id: string
  voice: number
  status: 'suggested' | 'confirmed'
  similarity: number
  band: 'weak' | 'possible'
  provisional: boolean
  short_evidence?: boolean
  created_at: string | null
}

interface MatchAudio {
  audio_url: string | null
  audio_ms: number | null
  speech_ms: number | null
}

export interface RecordingVoiceMatch {
  search_id: string
  match_id: string
  status: VoicePairStatusDto
  similarity: number
  band: 'weak' | 'possible'
  provisional: boolean
  short_evidence?: boolean
  conditions: VoiceConditionDto[]
  subject: SubjectSummaryDto
  recording: MatchAudio & { document_id: string; title: string; voice: number }
  reference: MatchAudio & { voiceprint_id: string }
  member_role: CaseRole
}

export interface RecordingVoiceMatchDecision {
  match_id: string
  status: 'confirmed' | 'rejected'
}

export interface RecordingVoiceMatchLead extends Omit<RecordingVoiceMatchSummary, 'voice'> {
  subject: SubjectSummaryDto
  recording: { document_id: string; title: string; voice: number }
}

export interface RecordingVoiceMatchLeads {
  search_id: string
  items: RecordingVoiceMatchLead[]
  total: number
  member_role: CaseRole
}

const matchPath = (caseId: string, subjectRef: string, matchId: string) =>
  humintPath(caseId, 'subjects', subjectRef, 'audio', 'matches', matchId)

export const recordingVoiceMatchesApi = {
  leads: (caseId: string, signal?: AbortSignal) =>
    apiClient.get<RecordingVoiceMatchLeads>(humintPath(caseId, 'voice', 'recording-matches'), {
      signal,
    }),
  read: (caseId: string, subjectRef: string, matchId: string, signal?: AbortSignal) =>
    apiClient.get<RecordingVoiceMatch>(matchPath(caseId, subjectRef, matchId), { signal }),
  decide: (caseId: string, subjectRef: string, matchId: string, decision: 'same' | 'different') =>
    apiClient.put<RecordingVoiceMatchDecision>(
      `${matchPath(caseId, subjectRef, matchId)}/decision`,
      { decision }
    ),
}
